
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../LanguageContext';
import { useLocation } from 'react-router-dom';

const SCALE_OPTIONS = [1, 2, 3, 4, 5];

const CONSUMPTION_METHODS = ['Joint', 'Bong', 'Pipe', 'Vaporizer', 'Other'];
const DOSAGE_LEVELS = ['Low', 'Medium', 'High'];
const CONTEXT_OPTIONS = ['Alone', 'With company', 'Before sleep', 'Before work', 'On weekends'];

const BUD_SHAPES = [
  'Spherical',
  'Conical',
  'Spike / arrow',
  'Clumpy',
  'Uneven / wild',
  'Commercial (even, neat)',
];
const DENSITY_ON_SQUEEZE = ['Stone', 'Very dense', 'Medium', 'Airy', 'Fluffy'];
const TRICHOMES_CHARACTER = [
  'Dusty (crumbly)',
  'Greasy (sticky)',
  'Glassy (shiny)',
  'Matte',
  'Speckled',
  'Even',
];
const TRICHOME_COVERAGE = ['Low', 'Medium', 'High'];
const COLOR_PALETTE = [
  'Light green',
  'Dark green',
  'Purple',
  'Almost black',
  'Silver',
  'Orange pistils',
  'Red pistils',
  'Yellow pistils',
];
const FIRST_VISUAL_IMPRESSION = ['Wow', 'Very interesting', 'Normal', 'Nothing special'];

const AROMA_INTENSITY = ['Weak', 'Medium', 'Strong'];
const AROMA_OPENING = ['No change', 'Increases when broken', 'Increases when grinded'];
const AROMA_PROFILE = [
  'Diesel',
  'Skunk',
  'Rubber',
  'Glue',
  'Candy',
  'Fruity',
  'Creamy',
  'Vanilla',
  'Mint',
  'Eucalyptus',
  'Citrus',
  'Pine',
  'Berries',
  'Grape',
  'Mango',
  'Lychee',
  'Guava',
  'Passion fruit',
  'Earthy',
  'Woody',
  'Spicy',
  'Chemical',
  'Metal',
  'Fermentation',
  'Other',
];

const FLAVOR_MATCH = ['Yes', 'Partially', 'No'];
const FLAVOR_INTENSITY = ['Weak', 'Medium', 'Strong'];
const FLAVOR_DYNAMICS = [
  'Stays until the end',
  'Fades in the middle',
  'Only at the beginning',
];
const AFTERTASTE = ['Sweet', 'Sour', 'Gassy', 'Earthy', 'Minty', 'Metallic', 'Other'];
const THROAT_FEEL = ['Smooth', 'Normal', 'Harsh', 'Burns'];
const BURN_QUALITY = ['Excellent', 'Normal', 'Bad'];
const ASH_COLOR = ['White', 'Gray', 'Dark'];
const TAR_OILY_RING = ['None', 'Slight', 'Strong'];
const LOSES_FLAVOR = ['Yes', 'No'];
const NEGATIVE_SENSATIONS = ['Dries', 'Stings', 'Cough', 'Hard to draw', 'None'];

const EFFECT_TYPE = ['Indica', 'Sativa', 'Hybrid'];
const EFFECT_STRENGTH = ['Weak', 'Medium', 'Strong'];
const EFFECT_DIRECTION = ['Up', 'Neutral', 'Down', 'Wavy', 'Slow onset', 'Sharp start'];
const MENTAL_EFFECTS = [
  'Clarity/focus',
  'Improves mood',
  'Calms thoughts',
  'Speeds thoughts',
  'Fog',
  'Creativity',
  'Talkative',
  'Social',
  'Withdrawn',
];
const BODY_EFFECTS = [
  'Body relaxation',
  'Heaviness',
  'Eye pressure',
  'Soft warmth',
  'Numbness',
  'Tingling',
  'Body energy',
];
const FUNCTIONAL_EFFECTS = [
  'Pain relief',
  'Anti-stress',
  'Anti-anxiety',
  'Appetite increase',
  'Helps sleep',
  'Suitable for work',
  'Suitable for rest',
];
const DURATION = ['Short', 'Medium', 'Long'];
const TIME_OF_USE = ['Day', 'Evening', 'Night'];

const RECOGNIZABILITY = ['Yes', 'Rather yes', 'Rather no', 'No'];
const WHO_IS_IT_FOR = [
  'Beginners',
  'Experienced',
  'Medical users',
  'Day use',
  'Evening/sleep',
  'Social',
  'Solo',
];
const ASSOCIATION_POSITIONING = [
  'Commercial hit',
  'Boutique (niche)',
  'Exotic',
  'For connoisseurs',
  'Has potential but raw',
];

const STRENGTHS = ['Visual', 'Smell', 'Taste', 'Effect', 'Uniqueness', 'Commercial potential'];
const PRESERVE = ['Genetics', 'Terpenes', 'Structure', 'Effect', 'Overall feeling'];
const DOWNSIDES = [
  'Weak taste',
  'Effect fades quickly',
  'Heavy onset',
  'Irritates throat',
  'Too sharp',
  'Unclear effect',
  'No clear character',
  'Other',
];

const COMMERCIAL_POTENTIAL = ['Low', 'Medium', 'High'];
const WOULD_BUY_AGAIN = ['Yes, definitely', 'Maybe', 'No'];

const OPTION_KEY_MAP = {
  scale: {
    '1': '1',
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
  },
  consumptionMethod: {
    Joint: 'joint',
    Bong: 'bong',
    Pipe: 'pipe',
    Vaporizer: 'vaporizer',
    Other: 'other',
  },
  dosage: {
    Low: 'low',
    Medium: 'medium',
    High: 'high',
  },
  context: {
    Alone: 'alone',
    'With company': 'withCompany',
    'Before sleep': 'beforeSleep',
    'Before work': 'beforeWork',
    'On weekends': 'onWeekends',
  },
  budShape: {
    Spherical: 'spherical',
    Conical: 'conical',
    'Spike / arrow': 'arrow',
    Clumpy: 'clumpy',
    'Uneven / wild': 'wild',
    'Commercial (even, neat)': 'commercial',
  },
  densityOnSqueeze: {
    Stone: 'stone',
    'Very dense': 'veryDense',
    Medium: 'medium',
    Airy: 'airy',
    Fluffy: 'fluffy',
  },
  trichomesCharacter: {
    'Dusty (crumbly)': 'dusty',
    'Greasy (sticky)': 'greasy',
    'Glassy (shiny)': 'glassy',
    Matte: 'matte',
    Speckled: 'speckled',
    Even: 'even',
  },
  trichomeCoverage: {
    Low: 'low',
    Medium: 'medium',
    High: 'high',
  },
  colorPalette: {
    'Light green': 'lightGreen',
    'Dark green': 'darkGreen',
    Purple: 'purple',
    'Almost black': 'almostBlack',
    Silver: 'silver',
    'Orange pistils': 'orangePistils',
    'Red pistils': 'redPistils',
    'Yellow pistils': 'yellowPistils',
  },
  firstVisualImpression: {
    Wow: 'wow',
    'Very interesting': 'veryInteresting',
    Normal: 'normal',
    'Nothing special': 'nothingSpecial',
  },
  aromaIntensity: {
    Weak: 'weak',
    Medium: 'medium',
    Strong: 'strong',
  },
  aromaOpening: {
    'No change': 'noChange',
    'Increases when broken': 'increasesWhenBroken',
    'Increases when grinded': 'increasesWhenGrinded',
  },
  aromaProfile: {
    Diesel: 'diesel',
    Skunk: 'skunk',
    Rubber: 'rubber',
    Glue: 'glue',
    Candy: 'candy',
    Fruity: 'fruity',
    Creamy: 'creamy',
    Vanilla: 'vanilla',
    Mint: 'mint',
    Eucalyptus: 'eucalyptus',
    Citrus: 'citrus',
    Pine: 'pine',
    Berries: 'berries',
    Grape: 'grape',
    Mango: 'mango',
    Lychee: 'lychee',
    Guava: 'guava',
    'Passion fruit': 'passionFruit',
    Earthy: 'earthy',
    Woody: 'woody',
    Spicy: 'spicy',
    Chemical: 'chemical',
    Metal: 'metal',
    Fermentation: 'fermentation',
    Other: 'other',
  },
  flavorMatchesAroma: {
    Yes: 'yes',
    Partially: 'partially',
    No: 'no',
  },
  flavorIntensity: {
    Weak: 'weak',
    Medium: 'medium',
    Strong: 'strong',
  },
  flavorDynamics: {
    'Stays until the end': 'staysToEnd',
    'Fades in the middle': 'fadesMiddle',
    'Only at the beginning': 'onlyBeginning',
  },
  aftertaste: {
    Sweet: 'sweet',
    Sour: 'sour',
    Gassy: 'gassy',
    Earthy: 'earthy',
    Minty: 'minty',
    Metallic: 'metallic',
    Other: 'other',
  },
  throatFeel: {
    Smooth: 'smooth',
    Normal: 'normal',
    Harsh: 'harsh',
    Burns: 'burns',
  },
  burn: {
    Excellent: 'excellent',
    Normal: 'normal',
    Bad: 'bad',
  },
  ash: {
    White: 'white',
    Gray: 'gray',
    Dark: 'dark',
  },
  tarOilyRing: {
    None: 'none',
    Slight: 'slight',
    Strong: 'strong',
  },
  losesFlavor: {
    Yes: 'yes',
    No: 'no',
  },
  negativeSensations: {
    Dries: 'dries',
    Stings: 'stings',
    Cough: 'cough',
    'Hard to draw': 'hardToDraw',
    None: 'none',
  },
  effectType: {
    Indica: 'indica',
    Sativa: 'sativa',
    Hybrid: 'hybrid',
  },
  effectStrength: {
    Weak: 'weak',
    Medium: 'medium',
    Strong: 'strong',
  },
  direction: {
    Up: 'up',
    Neutral: 'neutral',
    Down: 'down',
    Wavy: 'wavy',
    'Slow onset': 'slowOnset',
    'Sharp start': 'sharpStart',
  },
  mentalEffects: {
    'Clarity/focus': 'clarityFocus',
    'Improves mood': 'improvesMood',
    'Calms thoughts': 'calmsThoughts',
    'Speeds thoughts': 'speedsThoughts',
    Fog: 'fog',
    Creativity: 'creativity',
    Talkative: 'talkative',
    Social: 'social',
    Withdrawn: 'withdrawn',
  },
  bodyEffects: {
    'Body relaxation': 'bodyRelaxation',
    Heaviness: 'heaviness',
    'Eye pressure': 'eyePressure',
    'Soft warmth': 'softWarmth',
    Numbness: 'numbness',
    Tingling: 'tingling',
    'Body energy': 'bodyEnergy',
  },
  functionalEffects: {
    'Pain relief': 'painRelief',
    'Anti-stress': 'antiStress',
    'Anti-anxiety': 'antiAnxiety',
    'Appetite increase': 'appetiteIncrease',
    'Helps sleep': 'helpsSleep',
    'Suitable for work': 'suitableForWork',
    'Suitable for rest': 'suitableForRest',
  },
  duration: {
    Short: 'short',
    Medium: 'medium',
    Long: 'long',
  },
  timeOfUse: {
    Day: 'day',
    Evening: 'evening',
    Night: 'night',
  },
  recognizability: {
    Yes: 'yes',
    'Rather yes': 'ratherYes',
    'Rather no': 'ratherNo',
    No: 'no',
  },
  whoIsItFor: {
    Beginners: 'beginners',
    Experienced: 'experienced',
    'Medical users': 'medicalUsers',
    'Day use': 'dayUse',
    'Evening/sleep': 'eveningSleep',
    Social: 'social',
    Solo: 'solo',
  },
  associationPositioning: {
    'Commercial hit': 'commercialHit',
    'Boutique (niche)': 'boutique',
    Exotic: 'exotic',
    'For connoisseurs': 'connoisseurs',
    'Has potential but raw': 'rawPotential',
  },
  strengths: {
    Visual: 'visual',
    Smell: 'smell',
    Taste: 'taste',
    Effect: 'effect',
    Uniqueness: 'uniqueness',
    'Commercial potential': 'commercialPotential',
  },
  preserve: {
    Genetics: 'genetics',
    Terpenes: 'terpenes',
    Structure: 'structure',
    Effect: 'effect',
    'Overall feeling': 'overallFeeling',
  },
  downsides: {
    'Weak taste': 'weakTaste',
    'Effect fades quickly': 'effectFadesQuickly',
    'Heavy onset': 'heavyOnset',
    'Irritates throat': 'irritatesThroat',
    'Too sharp': 'tooSharp',
    'Unclear effect': 'unclearEffect',
    'No clear character': 'noClearCharacter',
    Other: 'other',
  },
  commercialPotential: {
    Low: 'low',
    Medium: 'medium',
    High: 'high',
  },
  wouldBuyAgain: {
    'Yes, definitely': 'yesDefinitely',
    Maybe: 'maybe',
    No: 'no',
  },
};

const getOptionLabelForGroup = (t, group, value) => {
  if (value === undefined || value === null) return '';
  const map = OPTION_KEY_MAP[group];
  if (!map) return String(value);
  const key = map[String(value)] ?? map[value];
  if (!key) return String(value);
  return t(`reviewsAdmin.options.${group}.${key}`);
};

const emptyForm = {
  reviewName: '',
  strainId: '',
  customStrainName: '',
  customStrainCode: '',
  strainSelectionComment: '',
  tastingDate: '',
  authorName: '',
  basic: {
    consumptionMethod: [],
    consumptionMethodOtherText: '',
    dosage: [],
    context: [],
    comment: '',
  },
  visual: {
    visualScore: [],
    budShape: [],
    densityOnSqueeze: [],
    trichomesCharacter: [],
    trichomeCoverage: [],
    colorPalette: [],
    firstVisualImpression: [],
    visualComment: '',
  },
  aroma: {
    aromaIntensity: [],
    aromaOpening: [],
    aromaProfile: [],
    aromaProfileOtherText: '',
    comment: '',
  },
  flavor: {
    flavorMatchesAroma: [],
    flavorIntensity: [],
    flavorDynamics: [],
    aftertaste: [],
    aftertasteOtherText: '',
    comment: '',
  },
  smokeQuality: {
    throatFeel: [],
    burn: [],
    ash: [],
    tarOilyRing: [],
    losesFlavor: [],
    negativeSensations: [],
    comment: '',
  },
  effect: {
    effectType: [],
    effectStrength: [],
    direction: [],
    mentalEffects: [],
    bodyEffects: [],
    functionalEffects: [],
    duration: [],
    timeOfUse: [],
    comment: '',
  },
  scales: {
    summaryScores: {
      visual: [],
      aroma: [],
      flavor: [],
      effect: [],
      uniqueness: [],
    },
    recognizability: [],
    whoIsItFor: [],
    associationPositioning: [],
    comment: '',
  },
  points: {
    strengths: [],
    preserve: [],
    downsides: [],
    downsidesOtherText: '',
    comment: '',
  },
  final: {
    commercialPotential: '',
    wouldBuyAgain: '',
    summaryStrongest: '',
    summaryImprove: '',
    comment: '',
  },
};

const normalizeList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const toggleItem = (list, value) => {
  const normalized = normalizeList(list);
  return normalized.includes(value)
    ? normalized.filter((item) => item !== value)
    : [...normalized, value];
};

function RadioGroup({ name, label, options, value, onChange, required }) {
  return (
    <div className="mb-2">
      <div className="fw-semibold mb-1">
        {label}
        {required ? ' *' : ''}
      </div>
      <div className="d-flex flex-wrap gap-3">
        {options.map((opt, index) => (
          <label key={opt} className="form-check-label d-flex align-items-center gap-1">
            <input
              type="radio"
              className="form-check-input"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              required={required && index === 0}
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

function CheckboxGroup({ label, options, values, onChange, getLabel }) {
  const normalizedValues = normalizeList(values);
  return (
    <div className="mb-2">
      <div className="fw-semibold mb-1">{label}</div>
      <div className="d-flex flex-wrap gap-3">
        {options.map((opt) => {
          const optionValue = opt?.value ?? opt;
          const labelText =
            getLabel?.(optionValue) ?? opt?.label ?? String(optionValue ?? '');
          return (
            <label key={String(optionValue)} className="form-check-label d-flex align-items-center gap-1">
              <input
                type="checkbox"
                className="form-check-input"
                checked={normalizedValues.includes(optionValue)}
                onChange={() => onChange(toggleItem(normalizedValues, optionValue))}
              />
              {labelText}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function ReviewsAdminPage() {
  const { t } = useLang();
  const location = useLocation();
  const lastSearchRef = useRef('');
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const canManageReviews = role === 'superuser' || role === 'admin' || role === 'rd';
  const canDownloadPublic = canManageReviews;
  const canDownloadInternal = canManageReviews;
  const canDownloadExtended = canManageReviews;
  const [strains, setStrains] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [invalidFieldKey, setInvalidFieldKey] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState({
    strainId: '',
    from: '',
    to: '',
    effectStrength: '',
    commercialPotential: '',
    recognizability: '',
  });
  const optionLabel = useCallback(
    (group, value) => getOptionLabelForGroup(t, group, value),
    [t]
  );
  const requiredLabel = useCallback((label) => `${label} *`, []);
  const invalidGroupClass = useCallback(
    (id) => (invalidFieldKey === id ? 'border border-danger rounded p-2' : ''),
    [invalidFieldKey]
  );
  const formatOptionValues = useCallback(
    (group, value) => {
      if (Array.isArray(value)) {
        const labels = value
          .filter((item) => item !== undefined && item !== null && item !== '')
          .map((item) => optionLabel(group, item));
        return labels.length ? labels.join(', ') : '-';
      }
      if (value === undefined || value === null || value === '') return '-';
      return optionLabel(group, value);
    },
    [optionLabel]
  );
  const headers = token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : {};
  const strainSelectRef = useRef(null);
  const customStrainNameRef = useRef(null);
  const customStrainCodeRef = useRef(null);
  const tastingDateRef = useRef(null);
  const consumptionMethodRef = useRef(null);
  const aromaIntensityRef = useRef(null);
  const flavorMatchRef = useRef(null);
  const effectTypeRef = useRef(null);
  const summaryVisualRef = useRef(null);
  const summaryAromaRef = useRef(null);
  const summaryFlavorRef = useRef(null);
  const summaryEffectRef = useRef(null);
  const summaryUniquenessRef = useRef(null);
  const summaryScoreRefs = {
    visual: summaryVisualRef,
    aroma: summaryAromaRef,
    flavor: summaryFlavorRef,
    effect: summaryEffectRef,
    uniqueness: summaryUniquenessRef,
  };

  const fetchStrains = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/admin/strains', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStrains(Array.isArray(data) ? data : []);
    } catch (err) {
      setStrains([]);
    }
  }, [token]);

  const buildQuery = (items) => {
    const params = new URLSearchParams();
    Object.entries(items).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    const q = params.toString();
    return q ? `?${q}` : '';
  };

  const fetchReviews = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`/api/admin/reviews${buildQuery(filters)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      setMessage(t('reviewsAdmin.messages.loadFailed'));
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchStrains();
    fetchReviews();
  }, [fetchStrains, fetchReviews]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const strainId = params.get('strainId');
    const strainSlug = params.get('slug');
    let resolvedId = strainId || '';
    if (!resolvedId && strainSlug && strains.length > 0) {
      const match = strains.find((strain) => strain.slug === strainSlug);
      resolvedId = match?._id || '';
    }
    if (!resolvedId) return;
    if (lastSearchRef.current !== location.search) {
      lastSearchRef.current = location.search;
      setFilters((prev) => ({ ...prev, strainId: resolvedId }));
    }
  }, [location.search, strains]);

  const strainOptions = useMemo(
    () => strains.map((strain) => ({ value: strain._id, label: strain.name || strain.slug })),
    [strains]
  );

  if (!token || !canManageReviews) {
    return (
      <div className="container mt-3">
        <p>{t('reviewsAdmin.common.login')}...</p>
      </div>
    );
  }

  const updateForm = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const updateNestedScore = (key, value) => {
    setForm((prev) => ({
      ...prev,
      scales: {
        ...prev.scales,
        summaryScores: { ...prev.scales.summaryScores, [key]: value },
      },
    }));
  };

  const hasValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  };

  const getRequiredFieldDefinitions = () => {
    const hasStrainId = hasValue(form.strainId);
    const hasCustomName = hasValue(form.customStrainName);
    const consumptionMethods = normalizeList(form.basic.consumptionMethod);
    const aromaProfiles = normalizeList(form.aroma.aromaProfile);
    const aftertasteValues = normalizeList(form.flavor.aftertaste);
    const downsidesValues = normalizeList(form.points.downsides);
    const commentLabel = (sectionKey) =>
      `${t(`reviewsAdmin.sections.${sectionKey}`)} - ${t('reviewsAdmin.fields.comment')}`;
    const otherTextLabel = (fieldLabel) =>
      `${fieldLabel} - ${t('reviewsAdmin.placeholders.otherText')}`;

    return [
      {
        id: 'reviewName',
        label: t('reviewsAdmin.fields.reviewName'),
        isSatisfied: () => hasValue(form.reviewName),
      },
      {
        id: 'strainSelection',
        label: t('reviewsAdmin.fields.strainSelectionRequired'),
        isSatisfied: () => hasStrainId || hasCustomName,
      },
      {
        id: 'customStrainName',
        label: t('reviewsAdmin.fields.customStrainName'),
        isSatisfied: () => hasValue(form.customStrainName),
      },
      {
        id: 'customStrainCode',
        label: t('reviewsAdmin.fields.customStrainCode'),
        isSatisfied: () => hasValue(form.customStrainCode),
      },
      {
        id: 'strainSelection.comment',
        label: commentLabel('strainSelection'),
        isSatisfied: () => hasValue(form.strainSelectionComment),
      },
      {
        id: 'tastingDate',
        label: t('reviewsAdmin.fields.tastingDate'),
        isSatisfied: () => hasValue(form.tastingDate),
      },
      {
        id: 'authorName',
        label: t('reviewsAdmin.fields.authorNameOptional'),
        isSatisfied: () => hasValue(form.authorName),
      },
      {
        id: 'basic.consumptionMethod',
        label: t('reviewsAdmin.fields.consumptionMethod'),
        isSatisfied: () => hasValue(form.basic.consumptionMethod),
      },
      {
        id: 'basic.consumptionMethodOtherText',
        label: otherTextLabel(t('reviewsAdmin.fields.consumptionMethod')),
        isSatisfied: () =>
          !consumptionMethods.includes('Other') ||
          hasValue(form.basic.consumptionMethodOtherText),
      },
      {
        id: 'basic.dosage',
        label: t('reviewsAdmin.fields.dosage'),
        isSatisfied: () => hasValue(form.basic.dosage),
      },
      {
        id: 'basic.context',
        label: t('reviewsAdmin.fields.context'),
        isSatisfied: () => hasValue(form.basic.context),
      },
      {
        id: 'basic.comment',
        label: commentLabel('tastingMetadata'),
        isSatisfied: () => hasValue(form.basic.comment),
      },
      {
        id: 'visual.budShape',
        label: t('reviewsAdmin.fields.budShape'),
        isSatisfied: () => hasValue(form.visual.budShape),
      },
      {
        id: 'visual.densityOnSqueeze',
        label: t('reviewsAdmin.fields.densityOnSqueeze'),
        isSatisfied: () => hasValue(form.visual.densityOnSqueeze),
      },
      {
        id: 'visual.trichomesCharacter',
        label: t('reviewsAdmin.fields.trichomesCharacter'),
        isSatisfied: () => hasValue(form.visual.trichomesCharacter),
      },
      {
        id: 'visual.trichomeCoverage',
        label: t('reviewsAdmin.fields.trichomeCoverage'),
        isSatisfied: () => hasValue(form.visual.trichomeCoverage),
      },
      {
        id: 'visual.colorPalette',
        label: t('reviewsAdmin.fields.colorPalette'),
        isSatisfied: () => hasValue(form.visual.colorPalette),
      },
      {
        id: 'visual.firstVisualImpression',
        label: t('reviewsAdmin.fields.firstVisualImpression'),
        isSatisfied: () => hasValue(form.visual.firstVisualImpression),
      },
      {
        id: 'visual.comment',
        label: commentLabel('visual'),
        isSatisfied: () => hasValue(form.visual.visualComment),
      },
      {
        id: 'aroma.aromaIntensity',
        label: t('reviewsAdmin.fields.aromaIntensity'),
        isSatisfied: () => hasValue(form.aroma.aromaIntensity),
      },
      {
        id: 'aroma.aromaOpening',
        label: t('reviewsAdmin.fields.aromaOpening'),
        isSatisfied: () => hasValue(form.aroma.aromaOpening),
      },
      {
        id: 'aroma.aromaProfile',
        label: t('reviewsAdmin.fields.aromaProfile'),
        isSatisfied: () => hasValue(form.aroma.aromaProfile),
      },
      {
        id: 'aroma.aromaProfileOtherText',
        label: otherTextLabel(t('reviewsAdmin.fields.aromaProfile')),
        isSatisfied: () =>
          !aromaProfiles.includes('Other') || hasValue(form.aroma.aromaProfileOtherText),
      },
      {
        id: 'aroma.comment',
        label: commentLabel('aroma'),
        isSatisfied: () => hasValue(form.aroma.comment),
      },
      {
        id: 'flavor.flavorMatchesAroma',
        label: t('reviewsAdmin.fields.flavorMatchesAroma'),
        isSatisfied: () => hasValue(form.flavor.flavorMatchesAroma),
      },
      {
        id: 'flavor.flavorIntensity',
        label: t('reviewsAdmin.fields.flavorIntensity'),
        isSatisfied: () => hasValue(form.flavor.flavorIntensity),
      },
      {
        id: 'flavor.flavorDynamics',
        label: t('reviewsAdmin.fields.flavorDynamics'),
        isSatisfied: () => hasValue(form.flavor.flavorDynamics),
      },
      {
        id: 'flavor.aftertaste',
        label: t('reviewsAdmin.fields.aftertaste'),
        isSatisfied: () => hasValue(form.flavor.aftertaste),
      },
      {
        id: 'flavor.aftertasteOtherText',
        label: otherTextLabel(t('reviewsAdmin.fields.aftertaste')),
        isSatisfied: () =>
          !aftertasteValues.includes('Other') || hasValue(form.flavor.aftertasteOtherText),
      },
      {
        id: 'flavor.comment',
        label: commentLabel('flavor'),
        isSatisfied: () => hasValue(form.flavor.comment),
      },
      {
        id: 'smokeQuality.throatFeel',
        label: t('reviewsAdmin.fields.throatFeel'),
        isSatisfied: () => hasValue(form.smokeQuality.throatFeel),
      },
      {
        id: 'smokeQuality.burn',
        label: t('reviewsAdmin.fields.burn'),
        isSatisfied: () => hasValue(form.smokeQuality.burn),
      },
      {
        id: 'smokeQuality.ash',
        label: t('reviewsAdmin.fields.ash'),
        isSatisfied: () => hasValue(form.smokeQuality.ash),
      },
      {
        id: 'smokeQuality.tarOilyRing',
        label: t('reviewsAdmin.fields.tarOilyRing'),
        isSatisfied: () => hasValue(form.smokeQuality.tarOilyRing),
      },
      {
        id: 'smokeQuality.losesFlavor',
        label: t('reviewsAdmin.fields.losesFlavor'),
        isSatisfied: () => hasValue(form.smokeQuality.losesFlavor),
      },
      {
        id: 'smokeQuality.negativeSensations',
        label: t('reviewsAdmin.fields.negativeSensations'),
        isSatisfied: () => hasValue(form.smokeQuality.negativeSensations),
      },
      {
        id: 'smokeQuality.comment',
        label: commentLabel('smokeQuality'),
        isSatisfied: () => hasValue(form.smokeQuality.comment),
      },
      {
        id: 'effect.effectType',
        label: t('reviewsAdmin.fields.effectType'),
        isSatisfied: () => hasValue(form.effect.effectType),
      },
      {
        id: 'effect.effectStrength',
        label: t('reviewsAdmin.fields.effectStrength'),
        isSatisfied: () => hasValue(form.effect.effectStrength),
      },
      {
        id: 'effect.direction',
        label: t('reviewsAdmin.fields.direction'),
        isSatisfied: () => hasValue(form.effect.direction),
      },
      {
        id: 'effect.duration',
        label: t('reviewsAdmin.fields.duration'),
        isSatisfied: () => hasValue(form.effect.duration),
      },
      {
        id: 'effect.timeOfUse',
        label: t('reviewsAdmin.fields.timeOfUse'),
        isSatisfied: () => hasValue(form.effect.timeOfUse),
      },
      {
        id: 'effect.mentalEffects',
        label: t('reviewsAdmin.fields.mentalEffects'),
        isSatisfied: () => hasValue(form.effect.mentalEffects),
      },
      {
        id: 'effect.bodyEffects',
        label: t('reviewsAdmin.fields.bodyEffects'),
        isSatisfied: () => hasValue(form.effect.bodyEffects),
      },
      {
        id: 'effect.functionalEffects',
        label: t('reviewsAdmin.fields.functionalEffects'),
        isSatisfied: () => hasValue(form.effect.functionalEffects),
      },
      {
        id: 'effect.comment',
        label: commentLabel('effect'),
        isSatisfied: () => hasValue(form.effect.comment),
      },
      {
        id: 'scales.summaryScores.visual',
        label: t('reviewsAdmin.fields.summaryScores.visual'),
        isSatisfied: () => hasValue(form.scales.summaryScores.visual),
      },
      {
        id: 'scales.summaryScores.aroma',
        label: t('reviewsAdmin.fields.summaryScores.aroma'),
        isSatisfied: () => hasValue(form.scales.summaryScores.aroma),
      },
      {
        id: 'scales.summaryScores.flavor',
        label: t('reviewsAdmin.fields.summaryScores.flavor'),
        isSatisfied: () => hasValue(form.scales.summaryScores.flavor),
      },
      {
        id: 'scales.summaryScores.effect',
        label: t('reviewsAdmin.fields.summaryScores.effect'),
        isSatisfied: () => hasValue(form.scales.summaryScores.effect),
      },
      {
        id: 'scales.summaryScores.uniqueness',
        label: t('reviewsAdmin.fields.summaryScores.uniqueness'),
        isSatisfied: () => hasValue(form.scales.summaryScores.uniqueness),
      },
      {
        id: 'scales.recognizability',
        label: t('reviewsAdmin.fields.recognizability'),
        isSatisfied: () => hasValue(form.scales.recognizability),
      },
      {
        id: 'scales.whoIsItFor',
        label: t('reviewsAdmin.fields.whoIsItFor'),
        isSatisfied: () => hasValue(form.scales.whoIsItFor),
      },
      {
        id: 'scales.associationPositioning',
        label: t('reviewsAdmin.fields.associationPositioning'),
        isSatisfied: () => hasValue(form.scales.associationPositioning),
      },
      {
        id: 'points.strengths',
        label: t('reviewsAdmin.fields.strengths'),
        isSatisfied: () => hasValue(form.points.strengths),
      },
      {
        id: 'points.preserve',
        label: t('reviewsAdmin.fields.preserve'),
        isSatisfied: () => hasValue(form.points.preserve),
      },
      {
        id: 'points.downsides',
        label: t('reviewsAdmin.fields.downsides'),
        isSatisfied: () => hasValue(form.points.downsides),
      },
      {
        id: 'points.downsidesOtherText',
        label: otherTextLabel(t('reviewsAdmin.fields.downsides')),
        isSatisfied: () =>
          !downsidesValues.includes('Other') || hasValue(form.points.downsidesOtherText),
      },
      {
        id: 'final.commercialPotential',
        label: t('reviewsAdmin.fields.commercialPotential'),
        isSatisfied: () => hasValue(form.final.commercialPotential),
      },
      {
        id: 'final.wouldBuyAgain',
        label: t('reviewsAdmin.fields.wouldBuyAgain'),
        isSatisfied: () => hasValue(form.final.wouldBuyAgain),
      },
      {
        id: 'final.summaryStrongest',
        label: t('reviewsAdmin.fields.summaryStrongest'),
        isSatisfied: () => hasValue(form.final.summaryStrongest),
      },
      {
        id: 'final.summaryImprove',
        label: t('reviewsAdmin.fields.summaryImprove'),
        isSatisfied: () => hasValue(form.final.summaryImprove),
      },
      {
        id: 'final.comment',
        label: commentLabel('final'),
        isSatisfied: () => hasValue(form.final.comment),
      },
    ];
  };

  const getMissingFieldInfo = () =>
    getRequiredFieldDefinitions().filter((field) => !field.isSatisfied());

  const getMissingFields = () => getMissingFieldInfo().map((field) => field.label);

  useEffect(() => {
    if (!hasAttemptedSubmit) return;
    setMissingFields(getMissingFields());
  }, [form, t, hasAttemptedSubmit]);

  const scrollToFieldId = (id) => {
    if (typeof document === 'undefined') return;
    const target = document.querySelector(`[data-field-id="${id}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const focusTarget = target.matches('input, select, textarea')
      ? target
      : target.querySelector('input, select, textarea');
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setHasAttemptedSubmit(true);
    const missingInfo = getMissingFieldInfo();
    if (missingInfo.length > 0) {
      setMissingFields(missingInfo.map((field) => field.label));
      const firstMissingId = missingInfo[0].id;
      setInvalidFieldKey(firstMissingId);
      requestAnimationFrame(() => scrollToFieldId(firstMissingId));
      setTimeout(() => setInvalidFieldKey(null), 1500);
      setSaving(false);
      return;
    }
    const payload = {
      reviewName: form.reviewName,
      strainId: form.strainId,
      customStrainName: form.customStrainName,
      customStrainCode: form.customStrainCode,
      strainSelection: {
        strainId: form.strainId,
        customStrainName: form.customStrainName,
        customStrainCode: form.customStrainCode,
        comment: form.strainSelectionComment,
      },
      tastingDate: form.tastingDate,
      authorName: form.authorName,
      tasting: {
        tastingDate: form.tastingDate,
        authorName: form.authorName,
        comment: form.basic.comment,
      },
      basic: {
        consumptionMethod: {
          value: form.basic.consumptionMethod,
          otherText:
            form.basic.consumptionMethod.includes('Other')
              ? form.basic.consumptionMethodOtherText
              : '',
        },
        dosage: form.basic.dosage,
        context: form.basic.context,
        comment: form.basic.comment,
      },
      visual: {
        visualScore: [],
        budShape: form.visual.budShape,
        densityOnSqueeze: form.visual.densityOnSqueeze,
        trichomesCharacter: form.visual.trichomesCharacter,
        trichomeCoverage: form.visual.trichomeCoverage,
        colorPalette: form.visual.colorPalette,
        firstVisualImpression: form.visual.firstVisualImpression,
        visualComment: form.visual.visualComment,
        comment: form.visual.visualComment,
      },
      aroma: {
        aromaIntensity: form.aroma.aromaIntensity,
        aromaOpening: form.aroma.aromaOpening,
        aromaProfile: form.aroma.aromaProfile,
        aromaProfileOtherText: form.aroma.aromaProfile.includes('Other')
          ? form.aroma.aromaProfileOtherText
          : '',
        comment: form.aroma.comment,
      },
      flavor: {
        flavorMatchesAroma: form.flavor.flavorMatchesAroma,
        flavorIntensity: form.flavor.flavorIntensity,
        flavorDynamics: form.flavor.flavorDynamics,
        aftertaste: form.flavor.aftertaste,
        aftertasteOtherText: form.flavor.aftertaste.includes('Other')
          ? form.flavor.aftertasteOtherText
          : '',
        comment: form.flavor.comment,
      },
      smokeQuality: {
        throatFeel: form.smokeQuality.throatFeel,
        burn: form.smokeQuality.burn,
        ash: form.smokeQuality.ash,
        tarOilyRing: form.smokeQuality.tarOilyRing,
        losesFlavor: form.smokeQuality.losesFlavor,
        negativeSensations: form.smokeQuality.negativeSensations,
        comment: form.smokeQuality.comment,
      },
      effect: {
        effectType: form.effect.effectType,
        effectStrength: form.effect.effectStrength,
        direction: form.effect.direction,
        mentalEffects: form.effect.mentalEffects,
        bodyEffects: form.effect.bodyEffects,
        functionalEffects: form.effect.functionalEffects,
        duration: form.effect.duration,
        timeOfUse: form.effect.timeOfUse,
        comment: form.effect.comment,
      },
      scales: {
        summaryScores: form.scales.summaryScores,
        recognizability: form.scales.recognizability,
        whoIsItFor: form.scales.whoIsItFor,
        associationPositioning: form.scales.associationPositioning,
        comment: form.scales.comment,
      },
      points: {
        strengths: form.points.strengths,
        preserve: form.points.preserve,
        downsides: form.points.downsides,
        downsidesOtherText: form.points.downsides.includes('Other')
          ? form.points.downsidesOtherText
          : '',
        comment: form.points.comment,
      },
      final: {
        commercialPotential: form.final.commercialPotential,
        wouldBuyAgain: form.final.wouldBuyAgain,
        summaryStrongest: form.final.summaryStrongest,
        summaryImprove: form.final.summaryImprove,
        comment: form.final.comment,
      },
    };

    try {
      const method = editingReviewId ? 'PUT' : 'POST';
      const url = editingReviewId
        ? `/api/admin/reviews/${editingReviewId}`
        : '/api/admin/reviews';
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessage(err.error || t('reviewsAdmin.messages.saveFailed'));
      } else {
        setMessage(t('reviewsAdmin.messages.saved'));
        setForm(emptyForm);
        setEditingReviewId(null);
        setMissingFields([]);
        setHasAttemptedSubmit(false);
        await fetchReviews();
      }
    } catch (err) {
      setMessage(t('reviewsAdmin.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm(t('reviewsAdmin.messages.confirmDelete'))) return;
    await fetch(`/api/admin/reviews/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchReviews();
  };

  const toDateInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  };

  const mapReviewToForm = (review) => ({
    reviewName: review?.reviewName || '',
    strainId: review?.strainId?._id || review?.strainId || '',
    customStrainName: review?.customStrainName || review?.strainSelection?.customStrainName || '',
    customStrainCode: review?.customStrainCode || review?.strainSelection?.customStrainCode || '',
    strainSelectionComment: review?.strainSelection?.comment || '',
    tastingDate: toDateInput(review?.tastingDate || review?.tasting?.tastingDate),
    authorName: review?.authorName || review?.tasting?.authorName || '',
    basic: {
      consumptionMethod:
        review?.basic?.consumptionMethod?.value || review?.basic?.consumptionMethod || [],
      consumptionMethodOtherText:
        review?.basic?.consumptionMethod?.otherText ||
        review?.basic?.consumptionMethodOtherText ||
        '',
      dosage: review?.basic?.dosage || [],
      context: review?.basic?.context || [],
      comment: review?.basic?.comment || '',
    },
    visual: {
      visualScore: [],
      budShape: review?.visual?.budShape || [],
      densityOnSqueeze: review?.visual?.densityOnSqueeze || [],
      trichomesCharacter: review?.visual?.trichomesCharacter || [],
      trichomeCoverage: review?.visual?.trichomeCoverage || [],
      colorPalette: review?.visual?.colorPalette || [],
      firstVisualImpression: review?.visual?.firstVisualImpression || [],
      visualComment: review?.visual?.visualComment || review?.visual?.comment || '',
    },
    aroma: {
      aromaIntensity: review?.aroma?.aromaIntensity || [],
      aromaOpening: review?.aroma?.aromaOpening || [],
      aromaProfile: review?.aroma?.aromaProfile || [],
      aromaProfileOtherText: review?.aroma?.aromaProfileOtherText || '',
      comment: review?.aroma?.comment || '',
    },
    flavor: {
      flavorMatchesAroma: review?.flavor?.flavorMatchesAroma || [],
      flavorIntensity: review?.flavor?.flavorIntensity || [],
      flavorDynamics: review?.flavor?.flavorDynamics || [],
      aftertaste: review?.flavor?.aftertaste || [],
      aftertasteOtherText: review?.flavor?.aftertasteOtherText || '',
      comment: review?.flavor?.comment || '',
    },
    smokeQuality: {
      throatFeel: review?.smokeQuality?.throatFeel || [],
      burn: review?.smokeQuality?.burn || [],
      ash: review?.smokeQuality?.ash || [],
      tarOilyRing: review?.smokeQuality?.tarOilyRing || [],
      losesFlavor: review?.smokeQuality?.losesFlavor || [],
      negativeSensations: review?.smokeQuality?.negativeSensations || [],
      comment: review?.smokeQuality?.comment || '',
    },
    effect: {
      effectType: review?.effect?.effectType || [],
      effectStrength: review?.effect?.effectStrength || [],
      direction: review?.effect?.direction || [],
      mentalEffects: review?.effect?.mentalEffects || [],
      bodyEffects: review?.effect?.bodyEffects || [],
      functionalEffects: review?.effect?.functionalEffects || [],
      duration: review?.effect?.duration || [],
      timeOfUse: review?.effect?.timeOfUse || [],
      comment: review?.effect?.comment || '',
    },
    scales: {
      summaryScores: {
        visual: review?.scales?.summaryScores?.visual || [],
        aroma: review?.scales?.summaryScores?.aroma || [],
        flavor: review?.scales?.summaryScores?.flavor || [],
        effect: review?.scales?.summaryScores?.effect || [],
        uniqueness: review?.scales?.summaryScores?.uniqueness || [],
      },
      recognizability: review?.scales?.recognizability || [],
      whoIsItFor: review?.scales?.whoIsItFor || [],
      associationPositioning: review?.scales?.associationPositioning || [],
      comment: review?.scales?.comment || '',
    },
    points: {
      strengths: review?.points?.strengths || [],
      preserve: review?.points?.preserve || [],
      downsides: review?.points?.downsides || [],
      downsidesOtherText: review?.points?.downsidesOtherText || '',
      comment: review?.points?.comment || '',
    },
    final: {
      commercialPotential: review?.final?.commercialPotential || [],
      wouldBuyAgain: review?.final?.wouldBuyAgain || [],
      summaryStrongest: review?.final?.summaryStrongest || '',
      summaryImprove: review?.final?.summaryImprove || '',
      comment: review?.final?.comment || '',
    },
  });

  const duplicateReview = async (id) => {
    if (!token) return;
    setMessage('');
    try {
      const res = await fetch(`/api/admin/reviews/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setMessage(t('reviewsAdmin.messages.saveFailed'));
        return;
      }
      const data = await res.json();
      setForm(mapReviewToForm(data));
      setEditingReviewId(data?._id || null);
      setMissingFields([]);
      setHasAttemptedSubmit(false);
      await fetchReviews();
    } catch (err) {
      setMessage(t('reviewsAdmin.messages.saveFailed'));
    }
  };

  const editReview = async (id) => {
    if (!token) return;
    setMessage('');
    try {
      const res = await fetch(`/api/admin/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setMessage(t('reviewsAdmin.messages.loadFailed'));
        return;
      }
      const data = await res.json();
      setForm(mapReviewToForm(data));
      setEditingReviewId(data?._id || null);
      setMissingFields([]);
      setHasAttemptedSubmit(false);
    } catch (err) {
      setMessage(t('reviewsAdmin.messages.loadFailed'));
    }
  };

  const downloadReview = async (id, type) => {
    if (!token) return;
    setMessage('');
    try {
      const res = await fetch(`/api/reviews/${id}/download/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setMessage(t('reviewsAdmin.messages.downloadFailed'));
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `review-${id}-${type}.txt`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage(t('reviewsAdmin.messages.downloadFailed'));
    }
  };

  return (
    <div className="container mt-3" style={{ maxWidth: 900 }}>
      <h1 className="mb-3">{t('reviewsAdmin.title')}</h1>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">{t('reviewsAdmin.sections.filters')}</h5>
          <div className="row g-2">
            <div className="col-12 col-md-4">
              <select
                className="form-select"
                value={filters.strainId}
                onChange={(e) => setFilters((f) => ({ ...f, strainId: e.target.value }))}
              >
                <option value="">{t('reviewsAdmin.placeholders.allStrains')}</option>
                {strainOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <input
                type="date"
                className="form-control"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              />
            </div>
            <div className="col-6 col-md-2">
              <input
                type="date"
                className="form-control"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              />
            </div>
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={filters.effectStrength}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, effectStrength: e.target.value }))
                }
              >
                <option value="">{t('reviewsAdmin.placeholders.effectStrength')}</option>
                {EFFECT_STRENGTH.map((opt) => (
                  <option key={opt} value={opt}>
                    {optionLabel('effectStrength', opt)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={filters.commercialPotential}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, commercialPotential: e.target.value }))
                }
              >
                <option value="">{t('reviewsAdmin.placeholders.commercialPotential')}</option>
                {COMMERCIAL_POTENTIAL.map((opt) => (
                  <option key={opt} value={opt}>
                    {optionLabel('commercialPotential', opt)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <select
                className="form-select"
                value={filters.recognizability}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, recognizability: e.target.value }))
                }
              >
                <option value="">{t('reviewsAdmin.placeholders.recognizability')}</option>
                {RECOGNIZABILITY.map((opt) => (
                  <option key={opt} value={opt}>
                    {optionLabel('recognizability', opt)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={fetchReviews}
              >
                {t('reviewsAdmin.actions.applyFilters')}
              </button>
            </div>
            <div className="col-12 col-md-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFilters({
                    strainId: '',
                    from: '',
                    to: '',
                    effectStrength: '',
                    commercialPotential: '',
                    recognizability: '',
                  });
                }}
              >
                {t('reviewsAdmin.actions.clearFilters')}
              </button>
            </div>
          </div>
          {loading && (
            <div className="mt-2 text-muted">
              <span className="spinner-border spinner-border-sm me-2" />
              {t('reviewsAdmin.common.loading')}
            </div>
          )}
          {message && <div className="mt-2">{message}</div>}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mb-4" noValidate>
        {hasAttemptedSubmit && missingFields.length > 0 && (
          <div className="alert alert-danger">
            <div className="fw-semibold mb-1">{t('reviewsAdmin.messages.missingFieldsTitle')}</div>
            <ul className="mb-0">
              {missingFields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">
              {requiredLabel(t('reviewsAdmin.sections.strainSelection'), 'strainSelection')}
            </h4>
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-6">
                <label className="form-label fw-semibold">
                  {t('reviewsAdmin.fields.reviewName')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  data-field-id="reviewName"
                  value={form.reviewName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, reviewName: e.target.value }))
                  }
                />
              </div>
              <div className="col-12 col-lg-6">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.strain')}</label>
                <select
                  className={`form-select ${
                    invalidFieldKey === 'strainSelection' ? 'is-invalid' : ''
                  }`}
                  data-field-id="strainSelection"
                  ref={strainSelectRef}
                  value={form.strainId}
                  onChange={(e) => setForm((prev) => ({ ...prev, strainId: e.target.value }))}
                >
                  <option value="">{t('reviewsAdmin.placeholders.selectStrain')}</option>
                  {strainOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold">
                  {t('reviewsAdmin.fields.customStrainName')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  data-field-id="customStrainName"
                  ref={customStrainNameRef}
                  value={form.customStrainName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, customStrainName: e.target.value }))
                  }
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold">
                  {t('reviewsAdmin.fields.customStrainCode')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  data-field-id="customStrainCode"
                  ref={customStrainCodeRef}
                  value={form.customStrainCode}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, customStrainCode: e.target.value }))
                  }
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="strainSelection.comment"
                  value={form.strainSelectionComment}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, strainSelectionComment: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.tastingMetadata')}</h4>
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">
                  {requiredLabel(t('reviewsAdmin.fields.tastingDate'), 'tastingDate')}
                </label>
                <input
                  type="date"
                  className={`form-control ${invalidFieldKey === 'tastingDate' ? 'is-invalid' : ''}`}
                  required
                  data-field-id="tastingDate"
                  ref={tastingDateRef}
                  value={form.tastingDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, tastingDate: e.target.value }))}
                />
              </div>
              <div className="col-12 col-md-8">
                <label className="form-label fw-semibold">
                  {t('reviewsAdmin.fields.authorNameOptional')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  data-field-id="authorName"
                  value={form.authorName}
                  onChange={(e) => setForm((prev) => ({ ...prev, authorName: e.target.value }))}
                />
              </div>
              <div className="col-12 col-lg-6">
                <div
                  ref={consumptionMethodRef}
                  className={invalidGroupClass('basic.consumptionMethod')}
                  data-field-id="basic.consumptionMethod"
                >
                  <CheckboxGroup
                    label={requiredLabel(
                      t('reviewsAdmin.fields.consumptionMethod'),
                      'basic.consumptionMethod'
                    )}
                    options={CONSUMPTION_METHODS}
                    values={form.basic.consumptionMethod}
                    onChange={(value) => updateForm('basic', 'consumptionMethod', value)}
                    getLabel={(value) => optionLabel('consumptionMethod', value)}
                  />
                  {form.basic.consumptionMethod.includes('Other') && (
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t('reviewsAdmin.placeholders.otherText')}
                      data-field-id="basic.consumptionMethodOtherText"
                      value={form.basic.consumptionMethodOtherText}
                      onChange={(e) =>
                        updateForm('basic', 'consumptionMethodOtherText', e.target.value)
                      }
                    />
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div data-field-id="basic.dosage">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.dosage')}
                    options={DOSAGE_LEVELS}
                    values={form.basic.dosage}
                    onChange={(value) => updateForm('basic', 'dosage', value)}
                    getLabel={(value) => optionLabel('dosage', value)}
                  />
                </div>
                <div data-field-id="basic.context">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.context')}
                    options={CONTEXT_OPTIONS}
                    values={form.basic.context}
                    onChange={(value) => updateForm('basic', 'context', value)}
                    getLabel={(value) => optionLabel('context', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="basic.comment"
                  value={form.basic.comment}
                  onChange={(e) => updateForm('basic', 'comment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.visual')}</h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div data-field-id="visual.budShape">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.budShape')}
                    options={BUD_SHAPES}
                    values={form.visual.budShape}
                    onChange={(value) => updateForm('visual', 'budShape', value)}
                    getLabel={(value) => optionLabel('budShape', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="visual.densityOnSqueeze">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.densityOnSqueeze')}
                    options={DENSITY_ON_SQUEEZE}
                    values={form.visual.densityOnSqueeze}
                    onChange={(value) => updateForm('visual', 'densityOnSqueeze', value)}
                    getLabel={(value) => optionLabel('densityOnSqueeze', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="visual.trichomesCharacter">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.trichomesCharacter')}
                    options={TRICHOMES_CHARACTER}
                    values={form.visual.trichomesCharacter}
                    onChange={(value) => updateForm('visual', 'trichomesCharacter', value)}
                    getLabel={(value) => optionLabel('trichomesCharacter', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="visual.trichomeCoverage">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.trichomeCoverage')}
                    options={TRICHOME_COVERAGE}
                    values={form.visual.trichomeCoverage}
                    onChange={(value) => updateForm('visual', 'trichomeCoverage', value)}
                    getLabel={(value) => optionLabel('trichomeCoverage', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="visual.colorPalette">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.colorPalette')}
                    options={COLOR_PALETTE}
                    values={form.visual.colorPalette}
                    onChange={(value) => updateForm('visual', 'colorPalette', value)}
                    getLabel={(value) => optionLabel('colorPalette', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <div data-field-id="visual.firstVisualImpression">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.firstVisualImpression')}
                    options={FIRST_VISUAL_IMPRESSION}
                    values={form.visual.firstVisualImpression}
                    onChange={(value) => updateForm('visual', 'firstVisualImpression', value)}
                    getLabel={(value) => optionLabel('firstVisualImpression', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="visual.comment"
                  value={form.visual.visualComment}
                  onChange={(e) => updateForm('visual', 'visualComment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.aroma')}</h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div
                  ref={aromaIntensityRef}
                  className={invalidGroupClass('aroma.aromaIntensity')}
                  data-field-id="aroma.aromaIntensity"
                >
                  <CheckboxGroup
                    label={requiredLabel(
                      t('reviewsAdmin.fields.aromaIntensity'),
                      'aroma.aromaIntensity'
                    )}
                    options={AROMA_INTENSITY}
                    values={form.aroma.aromaIntensity}
                    onChange={(value) => updateForm('aroma', 'aromaIntensity', value)}
                    getLabel={(value) => optionLabel('aromaIntensity', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="aroma.aromaOpening">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.aromaOpening')}
                    options={AROMA_OPENING}
                    values={form.aroma.aromaOpening}
                    onChange={(value) => updateForm('aroma', 'aromaOpening', value)}
                    getLabel={(value) => optionLabel('aromaOpening', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <div data-field-id="aroma.aromaProfile">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.aromaProfile')}
                    options={AROMA_PROFILE}
                    values={form.aroma.aromaProfile}
                    onChange={(value) => updateForm('aroma', 'aromaProfile', value)}
                    getLabel={(value) => optionLabel('aromaProfile', value)}
                  />
                </div>
                {form.aroma.aromaProfile.includes('Other') && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('reviewsAdmin.placeholders.otherText')}
                    data-field-id="aroma.aromaProfileOtherText"
                    value={form.aroma.aromaProfileOtherText}
                    onChange={(e) =>
                      updateForm('aroma', 'aromaProfileOtherText', e.target.value)
                    }
                  />
                )}
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="aroma.comment"
                  value={form.aroma.comment}
                  onChange={(e) => updateForm('aroma', 'comment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.flavor')}</h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div
                  ref={flavorMatchRef}
                  className={invalidGroupClass('flavor.flavorMatchesAroma')}
                  data-field-id="flavor.flavorMatchesAroma"
                >
                  <CheckboxGroup
                    label={requiredLabel(
                      t('reviewsAdmin.fields.flavorMatchesAroma'),
                      'flavor.flavorMatchesAroma'
                    )}
                    options={FLAVOR_MATCH}
                    values={form.flavor.flavorMatchesAroma}
                    onChange={(value) => updateForm('flavor', 'flavorMatchesAroma', value)}
                    getLabel={(value) => optionLabel('flavorMatchesAroma', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="flavor.flavorIntensity">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.flavorIntensity')}
                    options={FLAVOR_INTENSITY}
                    values={form.flavor.flavorIntensity}
                    onChange={(value) => updateForm('flavor', 'flavorIntensity', value)}
                    getLabel={(value) => optionLabel('flavorIntensity', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="flavor.flavorDynamics">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.flavorDynamics')}
                    options={FLAVOR_DYNAMICS}
                    values={form.flavor.flavorDynamics}
                    onChange={(value) => updateForm('flavor', 'flavorDynamics', value)}
                    getLabel={(value) => optionLabel('flavorDynamics', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="flavor.aftertaste">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.aftertaste')}
                    options={AFTERTASTE}
                    values={form.flavor.aftertaste}
                    onChange={(value) => updateForm('flavor', 'aftertaste', value)}
                    getLabel={(value) => optionLabel('aftertaste', value)}
                  />
                </div>
                {form.flavor.aftertaste.includes('Other') && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('reviewsAdmin.placeholders.otherText')}
                    data-field-id="flavor.aftertasteOtherText"
                    value={form.flavor.aftertasteOtherText}
                    onChange={(e) =>
                      updateForm('flavor', 'aftertasteOtherText', e.target.value)
                    }
                  />
                )}
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="flavor.comment"
                  value={form.flavor.comment}
                  onChange={(e) => updateForm('flavor', 'comment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.smokeQuality')}</h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div data-field-id="smokeQuality.throatFeel">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.throatFeel')}
                    options={THROAT_FEEL}
                    values={form.smokeQuality.throatFeel}
                    onChange={(value) => updateForm('smokeQuality', 'throatFeel', value)}
                    getLabel={(value) => optionLabel('throatFeel', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="smokeQuality.burn">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.burn')}
                    options={BURN_QUALITY}
                    values={form.smokeQuality.burn}
                    onChange={(value) => updateForm('smokeQuality', 'burn', value)}
                    getLabel={(value) => optionLabel('burn', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="smokeQuality.ash">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.ash')}
                    options={ASH_COLOR}
                    values={form.smokeQuality.ash}
                    onChange={(value) => updateForm('smokeQuality', 'ash', value)}
                    getLabel={(value) => optionLabel('ash', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="smokeQuality.tarOilyRing">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.tarOilyRing')}
                    options={TAR_OILY_RING}
                    values={form.smokeQuality.tarOilyRing}
                    onChange={(value) => updateForm('smokeQuality', 'tarOilyRing', value)}
                    getLabel={(value) => optionLabel('tarOilyRing', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="smokeQuality.losesFlavor">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.losesFlavor')}
                    options={LOSES_FLAVOR}
                    values={form.smokeQuality.losesFlavor}
                    onChange={(value) => updateForm('smokeQuality', 'losesFlavor', value)}
                    getLabel={(value) => optionLabel('losesFlavor', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="smokeQuality.negativeSensations">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.negativeSensations')}
                    options={NEGATIVE_SENSATIONS}
                    values={form.smokeQuality.negativeSensations}
                    onChange={(value) =>
                      updateForm('smokeQuality', 'negativeSensations', value)
                    }
                    getLabel={(value) => optionLabel('negativeSensations', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="smokeQuality.comment"
                  value={form.smokeQuality.comment}
                  onChange={(e) => updateForm('smokeQuality', 'comment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.effect')}</h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div
                  ref={effectTypeRef}
                  className={invalidGroupClass('effect.effectType')}
                  data-field-id="effect.effectType"
                >
                  <CheckboxGroup
                    label={requiredLabel(
                      t('reviewsAdmin.fields.effectType'),
                      'effect.effectType'
                    )}
                    options={EFFECT_TYPE}
                    values={form.effect.effectType}
                    onChange={(value) => updateForm('effect', 'effectType', value)}
                    getLabel={(value) => optionLabel('effectType', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.effectStrength">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.effectStrength')}
                    options={EFFECT_STRENGTH}
                    values={form.effect.effectStrength}
                    onChange={(value) => updateForm('effect', 'effectStrength', value)}
                    getLabel={(value) => optionLabel('effectStrength', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.direction">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.direction')}
                    options={EFFECT_DIRECTION}
                    values={form.effect.direction}
                    onChange={(value) => updateForm('effect', 'direction', value)}
                    getLabel={(value) => optionLabel('direction', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.duration">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.duration')}
                    options={DURATION}
                    values={form.effect.duration}
                    onChange={(value) => updateForm('effect', 'duration', value)}
                    getLabel={(value) => optionLabel('duration', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.timeOfUse">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.timeOfUse')}
                    options={TIME_OF_USE}
                    values={form.effect.timeOfUse}
                    onChange={(value) => updateForm('effect', 'timeOfUse', value)}
                    getLabel={(value) => optionLabel('timeOfUse', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.mentalEffects">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.mentalEffects')}
                    options={MENTAL_EFFECTS}
                    values={form.effect.mentalEffects}
                    onChange={(value) => updateForm('effect', 'mentalEffects', value)}
                    getLabel={(value) => optionLabel('mentalEffects', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.bodyEffects">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.bodyEffects')}
                    options={BODY_EFFECTS}
                    values={form.effect.bodyEffects}
                    onChange={(value) => updateForm('effect', 'bodyEffects', value)}
                    getLabel={(value) => optionLabel('bodyEffects', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="effect.functionalEffects">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.functionalEffects')}
                    options={FUNCTIONAL_EFFECTS}
                    values={form.effect.functionalEffects}
                    onChange={(value) => updateForm('effect', 'functionalEffects', value)}
                    getLabel={(value) => optionLabel('functionalEffects', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="effect.comment"
                  value={form.effect.comment}
                  onChange={(e) => updateForm('effect', 'comment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.scales')}</h4>
            <div className="row g-3">
              <div className="col-12">
                <div className="row g-2">
                  {['visual', 'aroma', 'flavor', 'effect', 'uniqueness'].map((key) => (
                    <div
                      className={`col-6 col-md-4 ${invalidGroupClass(
                        `scales.summaryScores.${key}`
                      )}`}
                      data-field-id={`scales.summaryScores.${key}`}
                      key={key}
                      ref={summaryScoreRefs[key]}
                    >
                      <CheckboxGroup
                        label={requiredLabel(
                          t(`reviewsAdmin.fields.summaryScores.${key}`),
                          `scales.summaryScores.${key}`
                        )}
                        options={SCALE_OPTIONS}
                        values={form.scales.summaryScores[key]}
                        onChange={(value) => updateNestedScore(key, value)}
                        getLabel={(value) => optionLabel('scale', value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div data-field-id="scales.recognizability">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.recognizability')}
                    options={RECOGNIZABILITY}
                    values={form.scales.recognizability}
                    onChange={(value) => updateForm('scales', 'recognizability', value)}
                    getLabel={(value) => optionLabel('recognizability', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div data-field-id="scales.whoIsItFor">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.whoIsItFor')}
                    options={WHO_IS_IT_FOR}
                    values={form.scales.whoIsItFor}
                    onChange={(value) => updateForm('scales', 'whoIsItFor', value)}
                    getLabel={(value) => optionLabel('whoIsItFor', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <div data-field-id="scales.associationPositioning">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.associationPositioning')}
                    options={ASSOCIATION_POSITIONING}
                    values={form.scales.associationPositioning}
                    onChange={(value) => updateForm('scales', 'associationPositioning', value)}
                    getLabel={(value) => optionLabel('associationPositioning', value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.points')}</h4>
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <div data-field-id="points.strengths">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.strengths')}
                    options={STRENGTHS}
                    values={form.points.strengths}
                    onChange={(value) => updateForm('points', 'strengths', value)}
                    getLabel={(value) => optionLabel('strengths', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div data-field-id="points.preserve">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.preserve')}
                    options={PRESERVE}
                    values={form.points.preserve}
                    onChange={(value) => updateForm('points', 'preserve', value)}
                    getLabel={(value) => optionLabel('preserve', value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <div data-field-id="points.downsides">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.downsides')}
                    options={DOWNSIDES}
                    values={form.points.downsides}
                    onChange={(value) => updateForm('points', 'downsides', value)}
                    getLabel={(value) => optionLabel('downsides', value)}
                  />
                </div>
                {form.points.downsides.includes('Other') && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('reviewsAdmin.placeholders.otherText')}
                    data-field-id="points.downsidesOtherText"
                    value={form.points.downsidesOtherText}
                    onChange={(e) =>
                      updateForm('points', 'downsidesOtherText', e.target.value)
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="h5 mb-3">{t('reviewsAdmin.sections.final')}</h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div data-field-id="final.commercialPotential">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.commercialPotential')}
                    options={COMMERCIAL_POTENTIAL}
                    values={form.final.commercialPotential}
                    onChange={(value) => updateForm('final', 'commercialPotential', value)}
                    getLabel={(value) => optionLabel('commercialPotential', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div data-field-id="final.wouldBuyAgain">
                  <CheckboxGroup
                    label={t('reviewsAdmin.fields.wouldBuyAgain')}
                    options={WOULD_BUY_AGAIN}
                    values={form.final.wouldBuyAgain}
                    onChange={(value) => updateForm('final', 'wouldBuyAgain', value)}
                    getLabel={(value) => optionLabel('wouldBuyAgain', value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  {t('reviewsAdmin.fields.summaryStrongest')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  data-field-id="final.summaryStrongest"
                  value={form.final.summaryStrongest}
                  onChange={(e) => updateForm('final', 'summaryStrongest', e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  {t('reviewsAdmin.fields.summaryImprove')}
                </label>
                <input
                  type="text"
                  className="form-control"
                  data-field-id="final.summaryImprove"
                  value={form.final.summaryImprove}
                  onChange={(e) => updateForm('final', 'summaryImprove', e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t('reviewsAdmin.fields.comment')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  data-field-id="final.comment"
                  value={form.final.comment}
                  onChange={(e) => updateForm('final', 'comment', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
            {saving ? t('reviewsAdmin.common.loading') : t('reviewsAdmin.common.save')}
          </button>
        </div>
      </form>

      <div className="card">
        <div className="card-body">
          <h4 className="h5 mb-3">{t('reviewsAdmin.sections.savedReviews')}</h4>
          {reviews.length === 0 ? (
            <p className="text-muted mb-0">{t('reviewsAdmin.common.noData')}</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>{t('reviewsAdmin.fields.reviewName')}</th>
                    <th>{t('reviewsAdmin.table.strain')}</th>
                    <th>{t('reviewsAdmin.table.date')}</th>
                    <th>{t('reviewsAdmin.table.author')}</th>
                    <th>{t('reviewsAdmin.table.effectStrength')}</th>
                    <th>{t('reviewsAdmin.table.commercialPotential')}</th>
                    <th>{t('reviewsAdmin.table.recognizability')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => (
                    <tr key={review._id}>
                      <td>{review.reviewName || '-'}</td>
                      <td>
                        {review.strainId?.name ||
                          review.strainId?.slug ||
                          review.customStrainName ||
                          review.strainSelection?.customStrainName ||
                          review.customStrainCode ||
                          review.strainSelection?.customStrainCode ||
                          '-'}
                      </td>
                      <td>
                        {review.tastingDate
                          ? new Date(review.tastingDate).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>{review.authorName || '-'}</td>
                      <td>{formatOptionValues('effectStrength', review.effect?.effectStrength)}</td>
                      <td>
                        {formatOptionValues('commercialPotential', review.final?.commercialPotential)}
                      </td>
                  <td>{formatOptionValues('recognizability', review.scales?.recognizability)}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          {canManageReviews && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => editReview(review._id)}
                            >
                              {t('reviewsAdmin.actions.edit')}
                            </button>
                          )}
                          {canManageReviews && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => duplicateReview(review._id)}
                            >
                              {t('reviewsAdmin.actions.duplicate')}
                            </button>
                          )}
                          {canDownloadPublic && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => downloadReview(review._id, 'public')}
                            >
                              {t('reviewsAdmin.actions.downloadPublic')}
                            </button>
                          )}
                          {canDownloadInternal && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => downloadReview(review._id, 'internal')}
                            >
                              {t('reviewsAdmin.actions.downloadInternal')}
                            </button>
                          )}
                          {canDownloadExtended && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => downloadReview(review._id, 'extended')}
                            >
                              {t('reviewsAdmin.actions.downloadExtended')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteReview(review._id)}
                          >
                            {t('reviewsAdmin.common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
